// Storm-phase taxonomy — pure, RN-portable. Turns the location's active NWS alerts
// into the three-rung escalation ladder and picks the single alert that drives each
// elevated screen. Severe convective ships first; Winter / Tropical extend the
// WARNING_RANK / WATCH_RANK tables with no change to the callers below.

import type { NwsAlert } from '@/lib/nws/types'

export type StormPhase = 'quiet' | 'watching' | 'storm'

/**
 * WARNING events that trigger the full red STORM MODE takeover, most-urgent first.
 * The first regex that matches an event sets its rank (lower index = higher rank).
 */
const WARNING_RANK: RegExp[] = [
  /tornado warning/i,
  /severe thunderstorm warning/i,
  /flash flood warning/i,
  /snow squall warning/i, // winter reuses this ladder next
  /extreme wind warning/i,
]

/** WATCH events that trigger the amber WATCHING state, most-urgent first. */
const WATCH_RANK: RegExp[] = [/tornado watch/i, /severe thunderstorm watch/i]

function rankIn(table: RegExp[], event: string): number {
  const i = table.findIndex((re) => re.test(event))
  return i === -1 ? Infinity : i
}

/** True when this alert is a STORM-MODE-worthy warning. */
export function isStormWarning(a: NwsAlert): boolean {
  if (a.messageType === 'Cancel') return false
  return rankIn(WARNING_RANK, a.event) !== Infinity
}

/** True when this alert is a WATCHING-worthy watch. */
export function isStormWatch(a: NwsAlert): boolean {
  if (a.messageType === 'Cancel') return false
  return rankIn(WATCH_RANK, a.event) !== Infinity
}

/**
 * A Tornado/Flash-Flood *Emergency* — the apex. NWS puts "EMERGENCY" wording in the
 * `description` (not the headline) and tags it `…DamageThreat: CATASTROPHIC`, so scan
 * both. Gated on severity Extreme to avoid matching the word "emergency" in boilerplate.
 */
export function isEmergency(a: NwsAlert): boolean {
  if (a.severity !== 'Extreme') return false
  const catastrophic = (k: string) => a.parameters?.[k]?.[0]?.toUpperCase() === 'CATASTROPHIC'
  return (
    /emergency/i.test(`${a.event} ${a.headline ?? ''} ${a.description ?? ''}`) ||
    catastrophic('tornadoDamageThreat') ||
    catastrophic('flashFloodDamageThreat')
  )
}

/** The single highest-priority active warning, or null. Emergencies always win. */
export function pickActiveWarning(alerts: NwsAlert[]): NwsAlert | null {
  const warnings = alerts.filter(isStormWarning)
  if (!warnings.length) return null
  return warnings.reduce((best, a) => {
    const ba = Number(isEmergency(a)) * -100 + rankIn(WARNING_RANK, a.event)
    const bb = Number(isEmergency(best)) * -100 + rankIn(WARNING_RANK, best.event)
    return ba < bb ? a : best
  })
}

/** The single highest-priority active watch, or null. */
export function pickActiveWatch(alerts: NwsAlert[]): NwsAlert | null {
  const watches = alerts.filter(isStormWatch)
  if (!watches.length) return null
  return watches.reduce((best, a) =>
    rankIn(WATCH_RANK, a.event) < rankIn(WATCH_RANK, best.event) ? a : best,
  )
}

/** The escalation rung for the location: warning ⇒ storm, else watch ⇒ watching, else quiet. */
export function stormPhase(alerts: NwsAlert[]): StormPhase {
  if (pickActiveWarning(alerts)) return 'storm'
  if (pickActiveWatch(alerts)) return 'watching'
  return 'quiet'
}

/** Is the warning a Severe Thunderstorm Warning? (Drives the YELLOW polygon; all else red.) */
export function isSvr(event: string): boolean {
  return /severe thunderstorm warning/i.test(event)
}
